import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaCalendarAlt, FaEnvelopeOpenText, FaGoogle, FaVideo, FaYoutube } from 'react-icons/fa';
import { pushEvent } from '../../utils/analytics';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { fetchCatalog } from '../../api/checkout';
import useABTest from '../../hooks/useABTest';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import {
  buildChallengeCalendarLinks,
  challengeSlugMatches,
  fetchBootcampOfferSummary,
  formatBootcampOfferDate,
  formatChallengeDateTime,
  pickKickoffAndDemo,
  youtubeLiveUrl,
} from './challengePageUtils';
import './ChallengeBookingPage.css';

const CALENDLY_URL =
  'https://calendly.com/lumiwealth/botspot-call?hide_event_type_details=1&hide_gdpr_banner=1';
const STARTER_CHECKOUT_PATH = '/checkout/starter-plan';

const readBookingContext = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem('challengeBookingContext') || '{}');
  } catch {
    return {};
  }
};

export default function ChallengeBookingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const widgetRef = useRef(null);
  const context = useMemo(readBookingContext, []);
  const [challenge, setChallenge] = useState(null);
  const [scheduleError, setScheduleError] = useState(false);
  const [bootcampOffer, setBootcampOffer] = useState(null);
  const [tuitionWindow, setTuitionWindow] = useState(null);

  // Post-registration ascension test: control books a call with Rob, test
  // offers the Starter plan directly. See src/utils/experiments.ts.
  const bookingExperiment = ACTIVE_EXPERIMENTS.CHALLENGE_BOOKING_CALL_VS_STARTER;
  const { isTest: isStarterVariant } = useABTest(
    bookingExperiment.name,
    bookingExperiment.featureFlag,
    'control',
    {
      page: 'challenge_booking',
      registration_id: context.registrationId || undefined,
      validVariants: ['control', 'test'],
      normalizeControlExposure: true,
    },
  );

  useEffect(() => {
    let mounted = true;
    fetchBootcampOfferSummary(fetchCatalog)
      .then((summary) => {
        if (!mounted) return;
        setBootcampOffer({
          name: summary.name,
          cohortName: summary.cohortName,
          startsAt: summary.startsAt,
        });
        setTuitionWindow(summary.tuitionWindow);
      })
      .catch(() => {
        if (!mounted) return;
        setBootcampOffer(null);
        setTuitionWindow(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const apiUrl = getApiBaseUrl() || 'https://api.botspot.trade';

    (async () => {
      try {
        const listResponse = await fetch(
          `${apiUrl}/courses/session-groups?active=true&registrationAccess=public&limit=50`,
        );
        if (!listResponse.ok) throw new Error(`session_groups_${listResponse.status}`);
        const listPayload = await listResponse.json();
        const groups = Array.isArray(listPayload)
          ? listPayload
          : listPayload.sessionGroups || listPayload.data || [];
        const match = groups.find((group) => challengeSlugMatches(group.name, slug));
        if (!match) throw new Error('challenge_not_found');

        const detailResponse = await fetch(`${apiUrl}/courses/session-groups/${match.id}`);
        const detail = detailResponse.ok ? await detailResponse.json() : match;
        if (!mounted) return;
        setChallenge({ ...match, ...detail });
        setScheduleError(false);
      } catch {
        if (!mounted) return;
        setScheduleError(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    pushEvent({
      event: 'challenge_booking_page_viewed',
      source: window.location.pathname,
      registration_id: context.registrationId || undefined,
    });
  }, [context.registrationId]);

  useEffect(() => {
    if (isStarterVariant) return undefined;
    let active = true;
    let initialized = false;

    const initialize = () => {
      if (!active || initialized || !window.Calendly || !widgetRef.current) return;
      initialized = true;
      window.Calendly.initInlineWidget({
        url: CALENDLY_URL,
        parentElement: widgetRef.current,
        prefill: {
          email: context.email || '',
          name: context.name || '',
        },
        utm: {
          utmSource: 'ai_trading_challenge',
          utmMedium: 'post_registration',
          utmCampaign: slug,
          utmContent: context.registrationId || '',
        },
      });
      pushEvent({
        event: 'calendly_widget_loaded',
        source: window.location.pathname,
        widget_url: CALENDLY_URL,
      });
    };

    const existingScript = document.querySelector('script[data-challenge-calendly="true"]');
    if (existingScript) {
      initialize();
      existingScript.addEventListener('load', initialize);
    } else {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.dataset.challengeCalendly = 'true';
      script.addEventListener('load', initialize);
      document.head.appendChild(script);
    }

    const handleCalendlyMessage = (event) => {
      if (event.origin !== 'https://calendly.com') return;
      if (event.data?.event !== 'calendly.event_scheduled') return;
      const inviteeUri = event.data?.payload?.invitee?.uri || null;
      const eventUri = event.data?.payload?.event?.uri || null;
      const inviteeId = inviteeUri?.split('/').filter(Boolean).pop() || null;
      const eventId = inviteeId ? `calendly-${inviteeId}` : null;
      window.sessionStorage.setItem(
        'challengeCallContext',
        JSON.stringify({
          inviteeUri,
          eventUri,
          slug,
        }),
      );
      pushEvent({
        event: 'expert_call_booked',
        source: window.location.pathname,
        calendly_event_type: 'botspot-call',
        registration_id: context.registrationId || undefined,
        event_id: eventId || undefined,
        transaction_id: eventId || undefined,
      });
      navigate(`/challenges/${slug}/call-confirmed`);
    };

    window.addEventListener('message', handleCalendlyMessage);
    initialize();

    return () => {
      active = false;
      window.removeEventListener('message', handleCalendlyMessage);
    };
  }, [context.email, context.name, context.registrationId, navigate, slug, isStarterVariant]);

  const { kickoff } = useMemo(() => pickKickoffAndDemo(challenge?.sessions || []), [challenge]);
  const apiUrl = getApiBaseUrl() || 'https://api.botspot.trade';
  const nextCohortDate = useMemo(
    () => formatBootcampOfferDate(bootcampOffer?.startsAt),
    [bootcampOffer?.startsAt],
  );
  const enrollmentEnds = useMemo(
    () => formatBootcampOfferDate(tuitionWindow?.endDate),
    [tuitionWindow?.endDate],
  );

  return (
    <main className="challenge-next" data-testid="challenge-booking-page">
      <Helmet>
        <title>Your challenge seat is saved | BotSpot</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <section className="challenge-next__intro">
        <p className="challenge-next__eyebrow">REGISTRATION CONFIRMED</p>
        <h1>Your challenge seat is saved.</h1>
        <p>Save the kickoff now so you know when and where to join.</p>
      </section>
      <section className="challenge-next__find-email" aria-labelledby="challenge-find-email-title">
        <div className="challenge-next__find-email-icon" aria-hidden="true">
          <FaEnvelopeOpenText />
        </div>
        <div>
          <h2 id="challenge-find-email-title">Go find our email right now</h2>
          <p>
            It probably landed in Promotions or Spam, not your main inbox. Open it, drag it into
            your main inbox (or mark it &quot;Not spam&quot;), and reply with the ticker you want
            the AI to look at first. That one move is what gets you tomorrow&apos;s kickoff link
            instead of missing it.
          </p>
        </div>
      </section>
      <section className="challenge-next__step" aria-labelledby="challenge-schedule-title">
        <div className="challenge-next__step-number" aria-hidden="true">
          1
        </div>
        <div className="challenge-next__step-body">
          <h2 id="challenge-schedule-title">Add the challenge to your calendar</h2>
          {kickoff ? (
            <ul className="challenge-next__sessions">
              {[kickoff].map((session) => {
                const calendarLinks = buildChallengeCalendarLinks(session, apiUrl);
                const youtubeUrl = youtubeLiveUrl(session);
                return (
                  <li key={session.id}>
                    <div className="challenge-next__session-title">
                      <strong>{session.title}</strong>
                      <span>{formatChallengeDateTime(session.scheduledAt)}</span>
                    </div>
                    <div className="challenge-next__session-actions">
                      {calendarLinks ? (
                        <>
                          <a
                            className="challenge-next__icon-action"
                            href={calendarLinks.google}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaGoogle aria-hidden="true" />
                            <span>Google Calendar</span>
                          </a>
                          <a
                            className="challenge-next__icon-action"
                            href={calendarLinks.ics}
                            download
                          >
                            <FaCalendarAlt aria-hidden="true" />
                            <span>Apple or Outlook</span>
                          </a>
                        </>
                      ) : null}
                      {session.joinUrl ? (
                        <a
                          className="challenge-next__icon-action"
                          href={session.joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaVideo aria-hidden="true" />
                          <span>Join on Zoom</span>
                        </a>
                      ) : null}
                      {youtubeUrl ? (
                        <a
                          className="challenge-next__icon-action"
                          href={youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaYoutube aria-hidden="true" />
                          <span>Watch on YouTube</span>
                        </a>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="challenge-next__status">
              {scheduleError
                ? 'Your seat is saved. Check your confirmation email for the live links.'
                : 'Loading your live schedule...'}
            </p>
          )}
        </div>
      </section>
      <section className="challenge-next__step" aria-labelledby="challenge-call-title">
        <div className="challenge-next__step-number" aria-hidden="true">
          2
        </div>
        <div className="challenge-next__step-body">
          {isStarterVariant ? (
            <>
              <h2 id="challenge-call-title">Want to start now instead of waiting for a call?</h2>
              <p>
                The Starter plan gets you live in BotSpot today: connect a broker, run your first
                AI-built strategy, and start the challenge with your account already set up.
              </p>
            </>
          ) : (
            <>
              <h2 id="challenge-call-title">Want me to look at your setup before we start?</h2>
              <p>Book a quick call with Rob to help you get started with AI trading.</p>
            </>
          )}
        </div>
      </section>
      {isStarterVariant ? (
        <div className="challenge-next__actions" aria-label="Start the Starter plan">
          <Link
            className="challenge-next__primary"
            to={`${STARTER_CHECKOUT_PATH}?utm_source=ai_trading_challenge&utm_medium=post_registration&utm_campaign=${slug}`}
            onClick={() =>
              pushEvent({
                event: 'starter_plan_offer_clicked',
                source: window.location.pathname,
                registration_id: context.registrationId || undefined,
              })
            }
          >
            Start the Starter plan
          </Link>
        </div>
      ) : (
        <section className="challenge-next__calendar" aria-label="Book a call with Rob">
          <div
            ref={widgetRef}
            className="challenge-next__calendly-mount"
            data-testid="challenge-calendly"
          />
        </section>
      )}
      {bootcampOffer ? (
        <p className="challenge-next__bootcamp-footer">
          Not booking a call right now? {bootcampOffer.cohortName || 'The next live Bootcamp'}
          {nextCohortDate ? ` starts ${nextCohortDate}` : ''}.{' '}
          <Link
            to={`/courses/ai-trading-bootcamp?utm_source=ai_trading_challenge&utm_medium=post_registration_footer&utm_campaign=${slug}`}
          >
            See what it covers
          </Link>
          {enrollmentEnds
            ? ` before ${tuitionWindow?.label || 'current pricing'} ends ${enrollmentEnds}.`
            : '.'}
        </p>
      ) : null}
      <Link className="challenge-next__skip" to={`/challenges/${slug}`}>
        Back to the challenge
      </Link>
    </main>
  );
}
